<template>
  <div :class="classes">
    <input class="lang_translate" type="checkbox" :id="props.id" :value="props.value" v-model="model" :disabled="disabled" />
    <span class="checkmark" aria-hidden="true"></span>
    <label class="lang_translate" v-if="props.large || props.full" :for="props.id"
      ><span>{{ props.label }}</span> <span> {{ props.label2 }}</span></label
    >
    <label v-else class="text lang_translate" :for="props.id">{{ props.label }}</label>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

const props = defineProps({
  id: String,
  label: String,
  label2: { type: String, required: false },
  value: String,
  disabled: Boolean,
  large: { type: Boolean, required: false, default: false },
  full: { type: Boolean, required: false, default: false },
});

const model = defineModel();

const classes = computed(() => ({
  toggle: true,
  disabled: props.disabled,
  'toggle--large': props.large,
  'toggle--full': props.full,
}));
</script>

<style lang="scss">
.toggle {
  display: block;
  position: relative;
  margin-bottom: 12px;
  font-size: 18px;
  user-select: none;

  &.disabled {
    opacity: 0.4;

    label {
      cursor: not-allowed;
    }
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    z-index: 1;
  }

  label {
    display: block;
    z-index: 2;
    width: 100%;
    padding-left: 50px;
    text-align: left;
    cursor: pointer;
    &:before {
      content: '';
      position: absolute;
      left: 0;
      width: 50px;
      height: 24px;
    }
  }

  .checkmark {
    display: block;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 24px;
    border-radius: 12px;
    background-color: rgb(var(--v-theme-grey-300));
    transition: background-color 0.3s ease;

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 4px;
      height: 16px;
      width: 16px;
      transform: translateY(-50%);
      border-radius: 50%;
      background-color: rgb(var(--v-theme-primary));
      transition: all 0.3s ease;
    }
  }

  input:checked ~ .checkmark {
    background-color: rgb(var(--v-theme-success));
  }

  input:checked ~ .checkmark:before {
    transform: translate(20px, -50%);
  }

  .checkmark:after {
    content: '';
    display: block;
    position: absolute;
    opacity: 0;
    transition: opacity 0.2s ease 0.1s, left 0.3s ease;
    top: 50%;
    left: 12px;
    transform: translate(-50%, -50%) rotate(45deg);
    margin-top: -1px;
    width: 4px;
    height: 10px;
    border: solid rgb(var(--v-theme-success));
    border-width: 0 2px 2px 0;
  }

  input:checked ~ .checkmark:after {
    opacity: 1;
    left: 32px;
  }

  &--large {
    display: flex;
    position: relative;
    width: 100%;
    max-width: 100%;

    label {
      z-index: 3;
      display: flex;
      height: 30px;
      padding-left: 0;
      text-transform: uppercase;
      font-family: 'Brandon Grotesque', sans-serif;
      font-weight: bold;
      letter-spacing: 0.5px;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 50%;
        font-size: 8px;
        line-height: 12px;
        transition: color 0.3s ease;
        color: rgb(var(--v-theme-white));
        + span {
          color: rgb(var(--v-theme-primary));
        }
      }

      @media screen and (min-width: $smaller-tablet-screen) {
        height: 40px;
        span {
          font-size: 12px;
        }
      }
    }

    .checkmark {
      width: 100%;
      height: 30px;
      border-radius: 18px;

      &:before {
        left: 0;
        height: 100%;
        width: 50%;
        border-radius: 20px;
      }
      &:after {
        display: none;
      }
      @media screen and (min-width: $smaller-tablet-screen) {
        height: 40px;
      }
    }

    input:checked ~ .checkmark {
      background-color: rgb(var(--v-theme-grey-300));
      &:before {
        transform: translate(100%, -50%);
      }
    }

    input:checked ~ label {
      span {
        color: rgb(var(--v-theme-primary));
        + span {
          color: rgb(var(--v-theme-white));
        }
      }
    }
  }

  &--full {
    display: flex;
    position: relative;
    width: 100%;
    max-width: 300px;

    label {
      z-index: 3;
      display: flex;
      height: 16px;
      padding-left: 0;
      text-transform: uppercase;
      font-family: 'Brandon Grotesque', sans-serif;
      font-weight: bold;
      letter-spacing: 0.5px;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 50%;
        font-size: 8px;
        line-height: 10px;
        transition: color 0.3s ease;
        color: rgb(var(--v-theme-white));
        + span {
          color: rgb(var(--v-theme-primary));
        }
      }

      @media screen and (min-width: $smaller-tablet-screen) {
        height: 18px;
        span {
          font-size: 10px;
        }
      }
    }

    .checkmark {
      width: 100%;
      height: 18px;
      border-radius: 18px;

      &:before {
        left: 0;
        height: 100%;
        width: 50%;
        border-radius: 20px;
      }
      &:after {
        display: none;
      }
      @media screen and (min-width: $smaller-tablet-screen) {
        height: 18px;
      }
    }

    input:checked ~ .checkmark {
      background-color: rgb(var(--v-theme-grey-300));
      &:before {
        transform: translate(100%, -50%);
      }
    }

    input:checked ~ label {
      span {
        color: rgb(var(--v-theme-primary));
        + span {
          color: rgb(var(--v-theme-white));
        }
      }
    }
  }
}
</style>
